import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';

/**
 * Normies Explainer layout — Educational card template.
 *
 * Clean information hierarchy: topic kicker, bold question title,
 * subtitle answer, body explanation, reference tags.
 * Normie image as a visual accent, not the hero.
 */
export function NormiesExplainerLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [imgLoaded, setImgLoaded] = useState(false);

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const bodySize = BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;

  const heroImage = data.normies?.heroImage;

  // Footer logic — mirrors Market (Dashboard) pattern
  const footerOn = data.footerEnabled !== false;
  // "None" = CTA text is empty (no Swipe/Numbering selected)
  const hasCta = !!(data.cta && data.cta.trim());

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  return (
    <>
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />

      <NudgeableGroup groupId="content" className="absolute top-[60px] left-[60px] right-[60px] bottom-[90px]" style={{ display: 'flex', flexDirection: 'column' }}>

        {/* Kicker — category label */}
        {data.kickerEnabled !== false && data.kicker && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(kickerSize, 18)}px`,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: '#60a5fa',
              textTransform: 'uppercase',
              marginBottom: '20px',
            }}
          >
            {data.kicker}
          </div>
        )}

        {/* Title — the big question */}
        <InlineEditable
          value={data.title}
          isEditing={editingField === 'title'}
          onChange={(html) => onEditChange && onEditChange('title', html)}
          onBlur={onEndEdit}
          className={`leading-[0.95] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
          style={{
            fontSize: `${Math.max(titleSize, 64)}px`,
            fontFamily: fontPreset.title,
            fontWeight: fontPreset.titleWeight || 800,
            color: pc.el.title || pc.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '-0.03em',
            marginBottom: '20px',
          }}
          onClick={handleTitleClick}
        >
          {data.title}
        </InlineEditable>

        {/* Subtitle — the one-liner answer */}
        {data.subtitleEnabled !== false && data.subtitle && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(subtitleSize, 20)}px`,
              color: pc.el.subtitle || pc.textMuted,
              marginBottom: '32px',
              letterSpacing: '0.03em',
              lineHeight: 1.3,
            }}
          >
            {data.subtitle}
          </div>
        )}

        {/* Center zone: Normie visual accent + body text */}
        <div style={{ flex: 1, display: 'flex', gap: '32px', alignItems: 'center' }}>
          {/* Normie — small visual accent */}
          {heroImage && (
            <div style={{
              width: '280px',
              height: '280px',
              flexShrink: 0,
              background: 'rgba(0,0,0,0.25)',
              border: `2px solid ${pc.accent(0.2)}`,
              overflow: 'hidden',
            }}>
              <img
                src={heroImage}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  imageRendering: 'pixelated',
                  opacity: imgLoaded ? 1 : 0,
                  transition: 'opacity 0.3s',
                }}
                onLoad={() => setImgLoaded(true)}
                crossOrigin="anonymous"
              />
            </div>
          )}

          {/* Body — the explanation */}
          {data.bodyEnabled !== false && data.body && (
            <div
              className="leading-[1.5]"
              style={{
                flex: 1,
                fontFamily: fontPreset.body || 'IBM Plex Mono, monospace',
                fontSize: `${Math.max(bodySize * 0.65, 18)}px`,
                color: pc.el.body || pc.textMuted,
                opacity: 0.9,
              }}
            >
              {renderFormattedText(data.body)}
            </div>
          )}
        </div>

        {/* Tags — reference labels */}
        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className={`flex flex-wrap items-center gap-3 mt-4${footerOn && hasCta ? ' mb-6' : ''}`}>
            {/* Footer ON + CTA "None": website address sits inline with tags */}
            {footerOn && !hasCta && (
              <span style={{ fontSize: '22px', color: pc.el.footer, marginRight: 'auto' }}>
                {data.ctaButton ?? 'magicforest.land'}
              </span>
            )}
            {/* Footer ON + CTA set: push tags right */}
            {footerOn && hasCta && <span style={{ flex: 1 }} />}
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(tagsSize, 12)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: '#60a5fa',
                  border: '1px solid rgba(96,165,250,0.3)',
                  padding: '5px 14px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </NudgeableGroup>

      {/* Footer separator + footer — only when footer is ON and CTA text is set */}
      {footerOn && hasCta && (
        <div className="absolute bottom-[36px] left-[60px] right-[60px]">
          <div style={{
            height: '1px',
            marginBottom: '14px',
            background: 'rgba(255,255,255,0.18)',
          }} />
          <SlideFooter data={data} pc={pc} />
        </div>
      )}
    </>
  );
}
